import {
  Box,
  Divider as MuiDivider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';

import { ILineItems } from '@/Billing/types';
import { OrderIcon } from '@/components/OrderIcon';
import { Unit as UnitType } from '@/Units/types';
import { naturalCompare } from '@/util';

type SortField = 'description' | 'type' | 'appliedTo' | 'cost';
type SortOrder = 'asc' | 'desc';

interface FeesTableProps {
  fees: ILineItems[];
  units: UnitType[];
}

const FeesTable: React.FC<FeesTableProps> = ({ fees, units }) => {
  const [orderBy, setOrderBy] = useState<SortField>('description');
  const [order, setOrder] = useState<SortOrder>('asc');

  const handleSort = (field: SortField) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder('asc');
    }
  };

  const sortedFees = useMemo(() => {
    return [...fees].sort((a, b) => {
      // derive the applied‐to name string
      const nameA = a.unitId ? units.find((u) => u.id === a.unitId)?.name ?? '' : 'All units';
      const nameB = b.unitId ? units.find((u) => u.id === b.unitId)?.name ?? '' : 'All units';

      let cmp = 0;
      switch (orderBy) {
        case 'description':
          cmp = naturalCompare(a.description, b.description);
          break;
        case 'type':
          cmp = naturalCompare(a.type, b.type);
          break;
        case 'appliedTo':
          cmp = naturalCompare(nameA, nameB);
          break;
        case 'cost':
          cmp = a.cost - b.cost;
          break;
      }
      return order === 'asc' ? cmp : -cmp;
    });
  }, [fees, units, orderBy, order]);

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        mt: 2,
        borderColor: 'divider',
        borderWidth: 2,
        borderRadius: 2,
        boxShadow: 'none',
      }}
    >
      <Box margin={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" id="tableTitle" component="div" fontWeight={600}>
            Fees
          </Typography>
        </Stack>
      </Box>
      <MuiDivider sx={{ mt: 1, borderColor: 'divider' }} />

      <Table size="medium">
        <TableHead>
          <TableRow sx={{ '& th': { borderBottom: `1px solid`, borderColor: 'divider' } }}>
            {[
              { field: 'description' as const, label: 'Description' },
              { field: 'type' as const, label: 'Type' },
              { field: 'appliedTo' as const, label: 'Applied to' },
              { field: 'cost' as const, label: 'Cost' },
            ].map(({ field, label }) => (
              <TableCell
                key={field}
                variant="head"
                align="center"
                sortDirection={orderBy === field ? order : false}
              >
                <TableSortLabel
                  active={orderBy === field}
                  direction={orderBy === field ? order : 'asc'}
                  onClick={() => handleSort(field)}
                  IconComponent={() => <OrderIcon type={orderBy === field ? order : ''} />}
                >
                  {label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedFees.map((fee, idx) => {
            const unitName = fee.unitId
              ? units.find((u) => u.id === fee.unitId)?.name || 'Unknown unit'
              : 'All units';
            return (
              <TableRow key={idx} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                <TableCell align="center">{fee.description}</TableCell>
                <TableCell align="center">{fee.type.replace('_', ' ')}</TableCell>
                <TableCell align="center">{unitName}</TableCell>
                <TableCell align="center">${fee.cost}</TableCell>
              </TableRow>
            );
          })}

          {fees.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                No fees added.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FeesTable;
